// QA harness for the handwriting pad under a SCALED ancestor.
//
// Regression surface for the founder bug "the tap-to-write pad only half-works
// on iPad/desktop (works on iPhone)". ROOT CAUSE: on the >=md page-fit path the
// session shell wraps content in FitToViewport's `transform: scale(s)` subtree
// (see src/components/FitToViewport.tsx, innerStyle). The pad's old fitCanvas
// mirrored container.getBoundingClientRect() (already the SCALED visual width)
// back into canvas.style.width/height, so the ancestor transform scaled the
// canvas a SECOND time — the canvas shrank to s x the visible pad, leaving a
// dead zone on the right/bottom where pointerdown never lands. iPhone (<md) has
// no transform, so it was unaffected.
//
// This route reproduces ONLY the load-bearing CSS condition (a `transform:
// scale(s)` ancestor — identical to what FitToViewport emits) so the e2e is
// deterministic (no viewport-measure timing). `?scale=` overrides the factor
// (default 0.7). e2e/60-handwriting-scale-coverage.spec.ts asserts the real
// HandwritingPad canvas still covers its container edge-to-edge under the
// transform (coverage ~1, not ~s). Bypasses the gate ladder via the App.jsx
// short-circuit, same as /qa-gallery + /qa-pal.

import { createFileRoute, useSearch } from "@tanstack/react-router";
import { HandwritingPad } from "@/components/HandwritingPad";

interface Search {
  scale?: number;
}

function QAHandwritingRoute() {
  const { scale } = useSearch({ from: "/qa-handwriting" });
  // Clamp to (0,1]; default 0.7 (a representative iPad fit scale, above
  // FitToViewport's MIN_SCALE=0.6 floor so the real app would be in scale
  // mode here, not scroll mode).
  const s = typeof scale === "number" && scale > 0 && scale <= 1 ? scale : 0.7;

  // R-1: belt-and-braces — even if a future router change reaches this
  // component outside the App.jsx DEV-gated bypass, never render the raw QA
  // surface in production.
  if (!import.meta.env.DEV) {
    return (
      <div className="sb-root flex min-h-[100dvh] items-center justify-center bg-background p-8 text-center">
        <p className="text-base text-muted-foreground">This page isn't available.</p>
      </div>
    );
  }

  return (
    <div className="sb-root min-h-[100dvh] bg-background p-6" data-testid="qa-handwriting-header">
      <h1 className="mb-4 font-serif text-2xl text-foreground">
        Handwriting pad — scaled ancestor ({s}x)
      </h1>
      {/* Mirrors FitToViewport's inner element: transform: scale(s) from the
          top center. The pad must keep a 1:1 canvas overlay inside it. */}
      <div
        data-testid="qa-hw-scaled"
        style={{ transform: `scale(${s})`, transformOrigin: "top center" }}
      >
        <div data-testid="qa-hw" className="mx-auto max-w-xl">
          <HandwritingPad aspect={2.4} guide="7" ariaLabel="QA handwriting pad" />
        </div>
      </div>
    </div>
  );
}

export const Route = createFileRoute("/qa-handwriting")({
  component: QAHandwritingRoute,
  validateSearch: (search: Record<string, unknown>): Search => ({
    scale: search.scale != null ? Number(search.scale) : undefined,
  }),
});
